import { useState, useEffect, useMemo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { useRequestSearchCountries } from '@/hooks/httpRequest';
import { useDebounce } from '@/hooks';
import { cn } from '@/lib/utils';
import { countryName } from '../constants';

interface CountryMultiSelectProps {
  showLabel?: boolean;
  name?: string;
}

const CountryMultiSelect = ({ showLabel = true, name = 'countryFilter' }: CountryMultiSelectProps) => {
  const { control, setValue } = useFormContext<any>();
  const countryFilter = useWatch({ control, name, defaultValue: [] });

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const { countries, isLoading } = useRequestSearchCountries(debouncedSearchTerm, open);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const selectedSet = useMemo(() => new Set(countryFilter), [countryFilter]);

  const toggleCountry = useCallback((iso3: string) => {
    const next = selectedSet.has(iso3)
      ? countryFilter.filter((c: string) => c !== iso3)
      : [...countryFilter, iso3];
    setValue(name, next, { shouldDirty: true });
  }, [name, selectedSet, countryFilter, setValue]);

  return (
    <div className='flex flex-col gap-1.5'>
      {showLabel && <label className='text-xs font-semibold text-muted-foreground'>Pays</label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn(
              'w-full justify-between bg-muted/30 focus:bg-background transition-colors h-9 md:h-10 font-normal px-3',
              countryFilter.length === 0 && 'text-muted-foreground'
            )}
          >
            {countryFilter.length > 0 ? (
              <span className='truncate text-sm'>{countryFilter.length} pays sélectionné(s)</span>
            ) : (
              'Rechercher un pays...'
            )}
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-(--radix-popover-trigger-width) p-0' align='start'>
          <div className='flex flex-col w-full bg-popover text-popover-foreground'>
            <div className='flex items-center border-b px-3'>
              <Search className='mr-2 h-4 w-4 shrink-0 opacity-50' />
              <input
                className='flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50'
                placeholder='Rechercher un pays...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {isLoading && <Loader2 className='ml-2 h-4 w-4 animate-spin opacity-50' />}
            </div>

            <div className='max-h-[200px] overflow-y-auto overflow-x-hidden p-1'>
              {!isLoading && debouncedSearchTerm.length > 0 && countries.length === 0 && (
                <div className='py-6 text-center text-sm text-muted-foreground'>Aucun pays trouvé.</div>
              )}
              {debouncedSearchTerm.length === 0 && (
                <div className='py-6 text-center text-sm text-muted-foreground'>Tapez pour rechercher un pays.</div>
              )}

              {countries.map(country => (
                <button
                  type='button'
                  key={country.iso3}
                  onClick={() => toggleCountry(country.iso3)}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground transition-colors text-left',
                    selectedSet.has(country.iso3) && 'bg-accent text-accent-foreground'
                  )}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', selectedSet.has(country.iso3) ? 'opacity-100' : 'opacity-0')} />
                  <span className='truncate'>{country.name}</span>
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {countryFilter.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-2'>
          {countryFilter.map((iso3: string) => (
            <span
              key={iso3}
              className='inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs'
            >
              {countryName(iso3)}
              <button
                type='button'
                className='ml-1 text-muted-foreground hover:text-foreground'
                onClick={() => toggleCountry(iso3)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {countryFilter.length === 0 && (
        <p className='text-[11px] text-muted-foreground'>Tous les pays inclus</p>
      )}
    </div>
  );
};

export default CountryMultiSelect;
